import {
    createBposResolver,
    getBposNavBarDataAsync,
    trySetBposNavBarData,
} from 'owa-bpos-store/lib/bposNavBarData';
import { getItem } from 'owa-local-storage';
import { isCapabilityEnabled } from 'owa-capabilities';
import { fetchBposDataOnBootCapability } from 'owa-capabilities-definitions/lib/fetchBposDataOnBootCapability';
import { LocalStoreLocaleKey, preloadLocStrings } from 'owa-localize-internal';
import type WebSessionType from 'owa-service/lib/contract/WebSessionType';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import type { StartConfig } from 'owa-shared-start';
import { preloadThemeImage, ThemeConstants } from 'owa-theme-shared';

const localStorageSource = 'ls';
const functionName = 'start';

export const getRunAfterRequests = (config: StartConfig) => {
    const oldRunAfterRequests = config.runAfterRequests;
    return (sessionDataPromise: Promise<SessionData>): void | undefined => {
        // try to preload the theme image from local storage
        const cachedThemeImage = getItem(window, ThemeConstants.LOCAL_IMAGE_STORAGE_KEY);
        if (cachedThemeImage) {
            preloadThemeImage(cachedThemeImage);
        }

        const cachedLocale = getItem(window, LocalStoreLocaleKey);
        if (cachedLocale) {
            document.documentElement.lang = cachedLocale;
        }

        preloadLocStrings(localStorageSource);

        // load the bpos data as soon as possible so we can start rendering the header as soon as possible
        if (config.loadBpos) {
            const resolveBpos = createBposResolver();
            sessionDataPromise.then(sd => {
                // we only call getBposNavBarDataAsync if the NavBarData is not already set
                // Bookings has a custom NavBarData that we don't want to overwrite
                const hasBposData = sd?.owaUserConfig?.NavBarData && config.app !== 'Bookings';

                // consumer users don't need NavBarData and we can set it to null
                const isConsumer = sd?.owaUserConfig?.SessionSettings?.WebSessionType !== 0;

                if (!isCapabilityEnabled(fetchBposDataOnBootCapability)) {
                    resolveBpos(() => trySetBposNavBarData(null));
                } else if (hasBposData || isConsumer) {
                    resolveBpos(() => trySetBposNavBarData(sd?.owaUserConfig?.NavBarData ?? null));
                } else {
                    resolveBpos(() => getBposNavBarDataAsync(functionName, config.app));
                }
            });
        }

        // Initialize localization once we have the user culture
        sessionDataPromise
            .then(() => {
                preloadLocStrings('sd');
            })
            .catch(() => {});

        return oldRunAfterRequests?.(sessionDataPromise);
    };
};
